import { useMemo } from "react";
import { createColumnHelper, useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Select, TextInputWithPrefix } from "../ui";
import "./_discount-editor.scss";

const DISCOUNT_TYPE_OPTIONS = [
  { label: "Flat", value: "Flat" },
  { label: "Percent", value: "Percent" },
];

const columnHelper = createColumnHelper();

/**
 * A single discount { type, value } - still a (single-row) table, matching
 * the other pricing editors on this step.
 *
 * Rendered via TanStack Table (column defs + flexRender) rather than a
 * hand-mapped `<tr>`, matching the other proposal-wizard tables - purely a
 * rendering-mechanism change, same markup/classNames/styling as before.
 *
 * Cells read `type`/`value` from TanStack's own row context
 * (`info.row.original`/`info.getValue()`), not from an outer closure -
 * matching LineItemsEditor/TaxEditor. Reading from closure used to make the
 * `columns` memo depend on `type`/`value` themselves, so every keystroke
 * rebuilt a brand-new `cell` function identity; React then saw a different
 * render function at that column position each time and remounted the real
 * `<input>` underneath it, dropping focus after every single character.
 */
const DiscountEditor = ({ discount, setDiscount, currencySign }) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor("type", {
        header: "Type",
        meta: { className: "discount-editor__col-type" },
        cell: (info) => (
          <Select
            compact
            options={DISCOUNT_TYPE_OPTIONS}
            value={info.row.original.type ?? "Flat"}
            onChange={(nextType) => setDiscount({ type: nextType, value: info.row.original.value ?? "" })}
          />
        ),
      }),
      columnHelper.accessor("value", {
        header: "Value",
        cell: (info) => (
          <TextInputWithPrefix
            compact
            prefix={info.row.original.type === "Percent" ? "%" : currencySign}
            placeholder={info.row.original.type === "Percent" ? "0" : "0.00"}
            value={info.getValue() ?? ""}
            onChange={(nextValue) => setDiscount({ type: info.row.original.type ?? "Flat", value: nextValue })}
          />
        ),
      }),
    ],
    [currencySign, setDiscount]
  );

  // A single fixed row - the discount object itself is the row data.
  const table = useReactTable({
    data: useMemo(() => [discount ?? {}], [discount]),
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="discount-editor">
      <h4 className="discount-editor__title">Discount</h4>
      <table className="discount-editor__rows">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={cell.column.columnDef.meta?.className}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DiscountEditor;
